import { useState, useEffect } from 'react';
import { useExperienceMode } from '@/contexts/ExperienceModeContext';
import { useLanguage } from '@/i18n/LanguageContext';

const AlternateModeToggle = () => {
  const { mode, isDiscovered, switchToMode, alternateVisits } = useExperienceMode();
  const { t } = useLanguage();
  const [isHovered, setIsHovered] = useState(false);
  const [showHint, setShowHint] = useState(false);

  // Show hint after user has visited alternate mode a few times
  useEffect(() => {
    if (alternateVisits >= 3 && !showHint) {
      const timer = setTimeout(() => setShowHint(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [alternateVisits, showHint]);

  if (!isDiscovered) return null;

  const isClassic = mode === 'classic';

  return (
    <div className="relative">
      <button
        onClick={() => switchToMode(isClassic ? 'alternate' : 'classic')}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          text-xs transition-all duration-500 
          ${isClassic 
            ? 'text-muted-foreground/40 hover:text-muted-foreground/70' 
            : 'text-muted-foreground/60 hover:text-foreground/80'
          }
          ${isHovered ? 'tracking-wider' : 'tracking-normal'}
        `}
        aria-label={isClassic ? t('switch_to_alternate') : t('switch_to_classic')}
      >
        <span className={`
          inline-block transition-transform duration-300
          ${isHovered ? (isClassic ? 'rotate-180' : '-rotate-180') : ''}
        `}>
          {isClassic ? '◇' : '◆'}
        </span>
        <span className="ml-2">
          {isClassic ? t('alternate_mode') : t('classic_mode')}
        </span>
      </button>

      {/* Subtle hint tooltip */}
      {showHint && isHovered && (
        <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap 
          text-[10px] text-muted-foreground/30 animate-fade-in">
          {isClassic 
            ? t('enter_parallel_reality') 
            : t('return_to_familiar')
          }
        </div>
      )}
    </div>
  );
};

export default AlternateModeToggle;
